import React, { useEffect, useRef } from "react";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import InputBase from "@mui/material/InputBase";
import IconButton from "@mui/material/IconButton";

const Search = styled("div")(() => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  borderRadius: 999,
  backgroundColor: "#f4f6f5",
  border: "1px solid #e4e7e6",
  color: "#1f1f1f",
  marginLeft: 0,
  width: "100%",
  minHeight: 42,
  "&:hover": {
    backgroundColor: "#eef2f1",
    borderColor: "#d5dcda",
  },
  "&:focus-within": {
    backgroundColor: "#fff",
    borderColor: "#075e54",
    boxShadow: "0 0 0 3px rgba(7, 94, 84, 0.12)",
  },
}));

const SearchIconWrapper = styled("div")(() => ({
  padding: "0 12px",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  color: "#075e54",
}));

const StyledInputBase = styled(InputBase)(() => ({
  color: "inherit",
  flex: 1,
  minWidth: 0,
  "& .MuiInputBase-input": {
    padding: "10px 12px 10px 0",
    width: "100%",
    fontSize: "0.92rem",
    "&::placeholder": {
      color: "#8a9391",
      opacity: 1,
    },
  },
}));

const SearchBar = ({
  placeholderText = "Search products, oils, herbs…",
  onSearchTextChange,
  onFocus,
  searchText,
  expandable = false,
  expanded = false,
  onExpand,
  onCollapse,
}) => {
  const inputRef = useRef(null);

  useEffect(() => {
    if (expandable && expanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [expandable, expanded]);

  const handleKeyDown = (event) => {
    if (event.key === "Escape" && expandable && onCollapse) {
      onCollapse();
    }
  };

  if (!expandable) {
    return (
      <Search className="header-search-field">
        <SearchIconWrapper>
          <SearchIcon fontSize="small" />
        </SearchIconWrapper>
        <StyledInputBase
          placeholder={placeholderText}
          inputProps={{ "aria-label": "search" }}
          onChange={onSearchTextChange}
          onFocus={onFocus}
          value={searchText}
        />
      </Search>
    );
  }

  return (
    <div
      className={`header-search ${expanded ? "is-expanded" : ""}`}
      onClick={() => {
        if (!expanded && onExpand) onExpand();
      }}
    >
      <button
        type="button"
        className="header-search-toggle"
        aria-label="search"
        title="Search"
        onClick={(event) => {
          event.stopPropagation();
          if (expanded) {
            inputRef.current?.focus();
            return;
          }
          if (onExpand) onExpand();
        }}
      >
        <SearchIcon className="header-search-toggle-icon" />
      </button>
      <StyledInputBase
        className="header-search-input"
        inputRef={inputRef}
        placeholder={placeholderText}
        inputProps={{ "aria-label": "search", "aria-hidden": !expanded }}
        onChange={onSearchTextChange}
        onFocus={onFocus}
        onKeyDown={handleKeyDown}
        value={searchText}
        tabIndex={expanded ? 0 : -1}
      />
      <IconButton
        aria-label="close search"
        aria-hidden={!expanded}
        size="small"
        className="header-search-close"
        tabIndex={expanded ? 0 : -1}
        onClick={(event) => {
          event.stopPropagation();
          if (onCollapse) onCollapse();
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </div>
  );
};

export default SearchBar;
